// External Imports
import { useEffect, useMemo, useRef, useState } from 'react';

// Icons
import { Archive03Icon, Delete02Icon } from '@hugeicons/core-free-icons';
import { FormTypes } from 'constants/forms';
import { useFormReplacement } from 'hooks/use-form-replacement';
import { useLocalStorage } from 'hooks/use-local-storage';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

// Components
import Alert from 'components/Alert/Alert';
import AutoSavedChip from 'components/AutoSavedChip/AutoSavedChip';
import EditorChromeLayout from 'components/EditorChromeLayout/EditorChromeLayout';
import FileSubmittionModal from 'components/FileSubmittionModal/FileSubmittionModal';
import { FormPreviewModal } from 'components/Form/FormEdit/FormPreviewModal/FormPreviewModal';
import { CustomToast } from 'components/Toast/CustomToast';

// Redux Selectors & Actions
import { selectAuthentication } from 'api/authentication-slice';
import { getBrands, selectBrands } from 'api/brands-slice';
import { getCompanyChecks, selectCompanyChecks } from 'api/company-checks-slice';
import { getCompany, selectCompany } from 'api/company-slice';
// RTK Query
import { useGetFormFromFileMutation, useGetFormQuery } from 'api/forms-api';
import { archiveForm, deleteForm, saveForm } from 'api/forms-slice';
import { getTeams, selectTeams } from 'api/teams-slice';

import QuestionnaireBuilder from './Components/QuestionnaireBuilder';
import QuestionnaireHeader from './Components/QuestionnaireHeader';
import QuestionnaireSidebar, { SIDEBAR_COLLAPSED_KEY } from './Components/QuestionnaireSideBar';

/** Normalise question text on save: trim and collapse spaces (for param-text fields). */
function normalizeParamText(value) {
  if (value == null || typeof value !== 'string') return value;
  return value.trim().replace(/\s+/g, ' ');
}

// Component
const QuestionnaireDetails = () => {
  // Local State

  const [loading, setLoading] = useState(true);
  const [alertType, setAlertType] = useState(null);
  const [aiGeneratorModalType, setAiGeneratorModalType] = useState(null);
  const [aiGenerating, setAiGenerating] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewFormValues, setPreviewFormValues] = useState(null);
  const [showAutoSavedChip, setShowAutoSavedChip] = useState(false);
  const [fieldsFromAIGenerator, setFieldsFromAIGenerator] = useState([]);
  const [sidebarCollapsed, setSidebarCollapsed] = useLocalStorage(SIDEBAR_COLLAPSED_KEY, false);
  const [showFormSettings, setShowFormSettings] = useState(false);

  // Hooks & Refs

  const { t } = useTranslation();
  const { id } = useParams();
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const builderRef = useRef(null);
  const formReplacement = useFormReplacement({
    onArchive: (_, replacements) => handleArchiveForm(replacements),
    onDelete: (_, replacements) => handleDeleteForm(replacements),
  });

  // API Hooks (skip when on /newform with copied form in state)
  // refetchOnMountOrArgChange: refetch when opening the page again or when id changes
  const { data: formFromApi, refetch: refetchForm } = useGetFormQuery(id, {
    skip: !id,
    refetchOnMountOrArgChange: true,
  });
  const formFromCopy = location.state?.form;
  const form = id ? formFromApi : formFromCopy;
  const [createFromFile] = useGetFormFromFileMutation();

  // Redux State

  const company = useSelector(selectCompany);
  const authentication = useSelector(selectAuthentication);
  const companyChecks = useSelector(selectCompanyChecks);
  const teams = useSelector(selectTeams) ?? [];
  const brands = useSelector(selectBrands);

  const isUsingModernForm = useMemo(() => {
    return (
      companyChecks?.find(check => check.type === 'custom_reference')?.configuration
        ?.use_new_form && form?.type === FormTypes.REFEREE
    );
  }, [companyChecks, form?.id]);

  const isUsingCustomReport = useMemo(() => {
    if (form?.type !== FormTypes.REFEREE) {
      return false;
    }
    return companyChecks?.find(check => check.type === 'custom_reference')?.configuration
      ?.in_custom_report;
  }, [companyChecks, form?.type]);

  const textParams = useMemo(() => {
    const candidateParams = {
      candidateFirstName: '{{candidateFirstName}}',
      candidateFullName: '{{candidateFullName}}',
      candidateLastName: '{{candidateLastName}}',
      job: '{{job}}',
      companyName: '{{companyName}}',
    };
    const refereeParams = {
      refereeFirstName: '{{refereeFirstName}}',
      refereeFullName: '{{refereeFullName}}',
      refereeLastName: '{{refereeLastName}}',
    };
    return {
      ...candidateParams,
      ...(form?.type === FormTypes.REFEREE ? refereeParams : {}),
    };
  }, [form?.type]);

  // Fetch Company if Not Loaded

  useEffect(() => {
    if (!company?.id && authentication?.company_id) {
      dispatch(getCompany(authentication.company_id));
    }
  }, [company, authentication, dispatch]);

  useEffect(() => {
    if (authentication?.company_id && (!companyChecks || companyChecks.length === 0)) {
      dispatch(getCompanyChecks());
    }
  }, [authentication?.company_id, companyChecks, dispatch]);

  useEffect(() => {
    if (authentication?.company_id) {
      dispatch(getTeams());
    }
  }, [authentication?.company_id, dispatch]);

  useEffect(() => {
    if (authentication?.company_id && brands.length === 0) {
      dispatch(getBrands());
    }
  }, [authentication?.company_id, brands, dispatch]);

  // Redirect /newform to questionnaires when opened without copied form in state

  useEffect(() => {
    if (location.pathname === '/newform' && !location.state?.form) {
      navigate('/questionnaires-v2', { replace: true });
    }
  }, [location.pathname, location.state?.form, navigate]);

  const defaultBrand = useMemo(() => {
    return brands.find(b => b.default);
  }, [brands]);

  // Loading State (when fetching by id, or when on /newform with no copied form)

  if (id && !formFromApi) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">Loading...</div>
    );
  }

  if (!id && !formFromCopy) {
    return null;
  }

  // Toast Helper

  const showToast = (type, title, message) => {
    toast.custom(() => <CustomToast t={t} title={title} message={message} type={type} />, {
      duration: 5000,
    });
  };

  // Save Feature

  const handleSaveForm = async (formData, options = {}) => {
    const isAutoSave = options.isAutoSave === true;
    setLoading(true);

    const normalizedData = {
      ...formData,
      message: formData.message ?? '',
      fields:
        formData.fields?.map(f => {
          const field = {
            ...f,
            text: normalizeParamText(f?.text),
          };
          if (formData.type !== FormTypes.REFEREE) {
            delete field.in_custom_report;
          }
          return field;
        }) ?? formData.fields,
    };

    try {
      const { payload } = await dispatch(saveForm(normalizedData));

      if (payload?.error) {
        showToast(
          'error',
          'Oops - something went wrong!',
          payload.error.message || 'Please try again.',
        );
        return false;
      }

      if (isAutoSave) {
        setShowAutoSavedChip(true);
      } else {
        showToast('success', 'Good Work!', 'Your form has been saved successfully.');
      }

      const savedForm = payload?.result?.form;
      const currentId = id ? Number(id) : null;
      const savedId = savedForm?.id != null ? Number(savedForm.id) : null;
      if (savedId && (!currentId || savedId !== currentId)) {
        navigate(`/questionnaire_details/${savedForm.id}`, { replace: true });
      } else if (currentId) {
        refetchForm();
      }

      return savedForm ?? true;
    } catch (error) {
      showToast('error', 'Oops - something went wrong!', error?.message || 'Please try again.');
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Delete Feature

  const handleDeleteForm = async replacements => {
    if (!form) return;
    try {
      setLoading(true);
      if (!replacements && (await formReplacement.intercept(form, 'delete'))) {
        return;
      }

      await dispatch(deleteForm({ form, replacements })).unwrap();

      showToast('success', 'Off to the bin!', 'Your form has been deleted successfully.');

      navigate('/questionnaires-v2');
      return true;
    } catch (error) {
      showToast('error', 'Oops - something went wrong!', error?.message || 'Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Archive Feature

  const handleArchiveForm = async replacements => {
    if (!form) return;
    try {
      setLoading(true);
      if (!replacements && (await formReplacement.intercept(form, 'archive'))) {
        return;
      }

      await dispatch(archiveForm({ form, replacements })).unwrap();

      showToast('success', 'Archived!', 'Your form has been archived successfully.');

      navigate('/questionnaires-v2');
      return true;
    } catch (error) {
      showToast('error', 'Oops - something went wrong!', error?.message || 'Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const copyFromQuestionnaire = copyFrom => {
    if (!copyFrom) return;

    const newForm = { ...copyFrom };
    delete newForm.id;

    newForm.name = (newForm.name || '') + ' (Copy)';
    newForm.fields = (newForm.fields || []).map(field => {
      const newField = { ...field };
      delete newField.id;
      return newField;
    });
    if (newForm.logic_jumps?.length) {
      newForm.logic_jumps = newForm.logic_jumps.map(lj => {
        const next = { ...lj };
        delete next.id;
        return next;
      });
    }

    navigate('/newform', { state: { form: newForm } });
  };

  // AI Generator - From Job Description

  const onCreateFromFormDescription = async fileData => {
    const questions = await createFromFile({
      from: 'job_description',
      file_data: fileData,
    }).unwrap();
    setFieldsFromAIGenerator(questions ?? []);
  };

  const onCreateFromFormQuestionnaire = async fileData => {
    const questions = await createFromFile({
      from: 'questionnaire',
      file_data: fileData,
    }).unwrap();
    setFieldsFromAIGenerator(questions ?? []);
  };

  const onCreateFromFile = async fileData => {
    const questions = await createFromFile({
      from: 'file',
      file_data: fileData,
    }).unwrap();
    setFieldsFromAIGenerator(questions ?? []);
  };

  // AI Generator Modal - single template with config

  const AI_GENERATOR_MODAL_CONFIG = {
    job_description: {
      titleKey: 'questionnaires.referencePlus.aiGenerator',
      descriptionKey: 'questionnaires.referencePlus.aiGeneratorDescriptionOne',
      onSubmit: onCreateFromFormDescription,
    },
    questionnaire: {
      titleKey: 'questionnaires.referencePlus.aiGenerator',
      descriptionKey: 'questionnaires.referencePlus.aiGeneratorDescriptionTwo',
      onSubmit: onCreateFromFormQuestionnaire,
    },
    file: {
      titleKey: 'questionnaires.referencePlus.aiGenerator',
      descriptionKey: 'questionnaires.aiGeneratorFromFileDescription',
      onSubmit: onCreateFromFile,
    },
  };

  const activeAiGeneratorModal = aiGeneratorModalType
    ? AI_GENERATOR_MODAL_CONFIG[aiGeneratorModalType]
    : null;

  const handleAiGeneratorSubmit = async fileData => {
    if (!activeAiGeneratorModal) return;
    setAiGenerating(true);
    try {
      await activeAiGeneratorModal.onSubmit(fileData);
      setAiGeneratorModalType(null);
    } catch (err) {
      showToast('error', 'Oops', err?.message || 'Failed to generate from file.');
    } finally {
      setAiGenerating(false);
    }
  };

  // Alert Configuration (Delete / Archive)

  const alertItemsData = {
    delete: {
      title: 'Are you sure you want to delete this questionnaire permanently?',
      message: 'Deletion cannot be undone. Are you sure you want to continue?',
      ok: 'Delete',
      onOk: () => {
        setAlertType(null);
        handleDeleteForm();
      },
      icon: Delete02Icon,
      showConfirmationInput: true,
      type: 'error',
    },
    archive: {
      title: 'Are you sure you want to archive this questionnaire?',
      message: 'Archiving cannot be undone. Are you sure you want to continue?',
      ok: 'Archive',
      onOk: () => {
        setAlertType(null);
        handleArchiveForm();
      },
      icon: Archive03Icon,
      showConfirmationInput: true,
      type: 'warning',
    },
  };

  const activeAlert = alertType ? alertItemsData[alertType] : null;

  // Render

  return (
    <>
      <AutoSavedChip visible={showAutoSavedChip} onHide={() => setShowAutoSavedChip(false)} />
      {formReplacement.modal}

      <FormPreviewModal
        visible={showPreview}
        onClose={() => setShowPreview(false)}
        form={previewFormValues ?? form}
        useModernForm={isUsingModernForm}
        allowTranslation={company?.ff_translations}
      />

      {activeAlert && (
        <Alert
          show={!!alertType}
          title={activeAlert.title}
          message={activeAlert.message}
          ok={activeAlert.ok}
          onOk={activeAlert.onOk}
          cancel="Cancel"
          icon={activeAlert.icon}
          showConfirmationInput={activeAlert.showConfirmationInput}
          type={activeAlert.type}
          onCancel={() => setAlertType(null)}
        />
      )}

      {activeAiGeneratorModal && (
        <FileSubmittionModal
          title={t(activeAiGeneratorModal.titleKey)}
          description={t(activeAiGeneratorModal.descriptionKey)}
          visible={!!aiGeneratorModalType}
          onClose={() => !aiGenerating && setAiGeneratorModalType(null)}
          onSubmit={handleAiGeneratorSubmit}
          isSubmitting={aiGenerating}
        />
      )}

      <EditorChromeLayout
        header={
          <QuestionnaireHeader
            form={form}
            company={company}
            aiGenerators={true}
            onAIQuestionnaireGenerator={() => setAiGeneratorModalType('questionnaire')}
            onJDAIGenerator={() => setAiGeneratorModalType('job_description')}
            onFromFileGenerator={() => setAiGeneratorModalType('file')}
            onCopy={() => copyFromQuestionnaire(form)}
            onArchive={() => setAlertType('archive')}
            onDelete={() => setAlertType('delete')}
            onRestore={() => {}}
            onTitleChange={name => builderRef.current?.setFormName(name)}
            onPreview={() => {
              setPreviewFormValues(builderRef.current?.getFormValues?.() ?? form);
              setShowPreview(true);
            }}
            onPublish={title => {
              if (title != null) builderRef.current?.setFormName(title);
              builderRef.current?.submit();
            }}
            onOpenSettings={() => setShowFormSettings(true)}
          />
        }
        sidebar={
          <QuestionnaireSidebar
            form={form}
            isCollapsed={sidebarCollapsed}
            onToggleCollapsed={setSidebarCollapsed}
            isUsingCustomReport={isUsingCustomReport}
          />
        }
        sidebarCollapsed={sidebarCollapsed}>
        <QuestionnaireBuilder
          ref={builderRef}
          form={form}
          fieldsFromAIGenerator={fieldsFromAIGenerator}
          onSave={handleSaveForm}
          textParams={textParams}
          sidebarCollapsed={sidebarCollapsed}
          isUsingModernForm={isUsingModernForm}
          showFormSettings={showFormSettings}
          onCloseFormSettings={() => setShowFormSettings(false)}
          teams={teams}
          isUsingCustomReport={isUsingCustomReport}
          defaultBrand={defaultBrand}
        />
      </EditorChromeLayout>
    </>
  );
};

export default QuestionnaireDetails;
